import React, { useCallback, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormsErrorParser } from 'src/containers/errors';
import {
  OrganizationTimeSlot,
  TimeSlotPlage,
} from 'src/features/DeliveryPublicColiInfo/delivery-public-colis-info.types';

type TimeSlotSelectorProps = {
  config: OrganizationTimeSlot[];
  error?: FieldError | undefined;
  onSelectionChange: (selectedSlots: OrganizationTimeSlot[]) => void;
};

export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({ error, config, onSelectionChange }) => {
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});

  const { t } = useTranslation();
  const toggleSlot = useCallback((day: string, slot: TimeSlotPlage) => {
    setSelected((prev) => {
      const daySlots = prev[day] || new Set();
      const slotKey = `${slot.from}-${slot.to}`;

      if (daySlots.has(slotKey)) {
        daySlots.delete(slotKey);
      } else {
        daySlots.add(slotKey);
      }

      const newSelected = { ...prev, [day]: new Set(daySlots) };
      if (daySlots.size === 0) delete newSelected[day]; // Cleanup if no slots remain
      return newSelected;
    });
  }, []);

  const isSelected = useCallback(
    (day: string, slot: TimeSlotPlage) => {
      const daySlots = selected[day] || new Set();
      return daySlots.has(`${slot.from}-${slot.to}`);
    },
    [selected],
  );

  useEffect(() => {
    const selectedSlots = Object.entries(selected).map(([day, slots]) => ({
      day,
      slots: Array.from(slots).map((key) => {
        const [from, to] = key.split('-');
        return { from, to };
      }),
    }));
    onSelectionChange(selectedSlots);
  }, [selected, onSelectionChange]);

  return (
    <div className={`p-6 bg-gray-50  ${error ? 'border-red-600 border-2' : ''}`}>
      <h2 className={`text-lg font-bold mb-4 ${error ? 'text-red-600' : ''}`}>
        {t('client-delivery-requests.time-slot')}
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {config.map(({ day, slots }) => (
          <div key={day} className="mb-6">
            <h3 className="text-md font-semibold mb-2 ">{t(`client-delivery-requests.${day}`)}</h3>
            {/* Notice Section */}
            <div className="flex flex-wrap gap-2">
              {slots.map((slot, index) => (
                <a
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleSlot(day, slot);
                  }}
                  className={`px-4 py-2 text-sm rounded-md inline-block cursor-pointer ${
                    isSelected(day, slot) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  {slot.from} - {slot.to}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-500 mb- italic6">{t('client-delivery-requests.time-slot-note')}</p>
      <div className="mt-2">
        <FormsErrorParser error={error} />
      </div>
    </div>
  );
};
